import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  CreditCard, Loader2, Check, AlertCircle, ArrowLeft,
  Trophy, ShoppingBag, User, Clock, ExternalLink, Truck, MapPin, Package
} from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

// PayPal Logo SVG
const PayPalLogo = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.27a.928.928 0 0 0-.915.772l-.95 6.003a.642.642 0 0 1-.633.533h-.696v.698z"/>
  </svg>
);

const OrderPaymentPage = ({ user }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  
  // Shipping state
  const [step, setStep] = useState('shipping'); // shipping, payment
  const [shippingAddress, setShippingAddress] = useState({
    name: user?.name || '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    phone: ''
  });
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
      
      if (response.data.status === 'paid') {
        setPaymentSuccess(true);
      }
      
      // If shipping address already set, skip to payment
      if (response.data.shipping_address) {
        setShippingAddress(response.data.shipping_address);
        if (response.data.shipping_option) {
          setSelectedShipping(response.data.shipping_option);
          setStep('payment');
        } else {
          // Fetch rates with existing address
          fetchShippingRates();
        }
      }
    } catch (err) {
      toast.error('Failed to load order details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setPaymentLoading(true);
    try {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) {
          toast.error('Payment status check timed out. Please refresh the page.');
          setPaymentLoading(false);
          return;
        }

        const response = await api.get(`/orders/${orderId}/payment-status?session_id=${sessionId}`);
        
        if (response.data.status === 'paid') {
          setPaymentSuccess(true);
          setPaymentLoading(false);
          toast.success('Payment successful!');
          fetchOrder();
          return;
        }
        
        setTimeout(() => pollStatus(attempts + 1), 2000);
      };

      await pollStatus();
    } catch (err) {
      console.error('Error checking payment:', err);
      setPaymentLoading(false);
    }
  };

  const saveShippingAddress = async () => {
    // Validate
    if (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city || 
        !shippingAddress.state || !shippingAddress.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoadingRates(true);
    try {
      const response = await api.post(`/orders/${orderId}/shipping-address`, shippingAddress);
      // Update order with tax info from response
      if (response.data.tax_info) {
        setOrder(prev => ({
          ...prev,
          tax_info: response.data.tax_info,
          tax_amount: response.data.tax_info.tax_amount
        }));
      }
      await fetchShippingRates();
    } catch (err) {
      toast.error('Failed to save shipping address');
    } finally {
      setLoadingRates(false);
    }
  };

  const fetchShippingRates = async () => {
    setLoadingRates(true);
    try {
      const response = await api.get(`/orders/${orderId}/shipping-rates`);
      setShippingRates(response.data.rates);
    } catch (err) {
      if (err.response?.data?.detail?.includes('address required')) {
        // Need to save address first
      } else {
        toast.error('Failed to fetch shipping rates');
      }
    } finally {
      setLoadingRates(false);
    }
  };

  const selectShipping = async (rate) => {
    try {
      const response = await api.post(`/orders/${orderId}/select-shipping`, {
        carrier: rate.carrier,
        service: rate.service,
        price: rate.price
      });
      setSelectedShipping(rate);
      setOrder(prev => ({
        ...prev,
        shipping_cost: rate.price,
        tax_amount: response.data.tax_amount || prev.tax_amount || 0,
        total_amount: response.data.total_amount
      }));
      setStep('payment');
      toast.success('Shipping method selected');
    } catch (err) {
      toast.error('Failed to select shipping');
    }
  };

  const handleStripePayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await api.post(`/orders/${orderId}/pay/stripe`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment session');
      setPaymentLoading(false);
    }
  };

  const handlePayPalPayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await api.post(`/orders/${orderId}/pay/paypal`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.approval_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create PayPal order');
      setPaymentLoading(false);
    }
  };

  const handlePayment = () => {
    if (selectedMethod === 'stripe') {
      handleStripePayment();
    } else {
      handlePayPalPayment();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Order Not Found</h2>
            <p className="text-gray-600 mb-4">This order doesn't exist or you don't have access to it.</p>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentSuccess || order.status === 'paid') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">
              Your payment has been processed. The seller has been notified and will ship your item soon.
            </p>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-4">
                <img 
                  src={order.listing_image} 
                  alt={order.listing_title}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div className="text-left">
                  <p className="font-medium">{order.listing_title}</p>
                  <p className="text-emerald-600 font-bold">${order.total_amount?.toFixed(2) || order.amount?.toFixed(2)}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="w-full bg-emerald-500 hover:bg-emerald-600"
              >
                View My Orders
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/live')}
                className="w-full"
              >
                Continue Shopping
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const taxAmount = order.tax_amount || 0;
  const totalAmount = (order.amount || 0) + (selectedShipping?.price || 0) + taxAmount;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="order-payment-page">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>

        {/* Winner Badge */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl p-4 mb-6 text-white">
          <div className="flex items-center gap-3">
            <Trophy className="h-8 w-8" />
            <div>
              <h2 className="font-bold text-lg">You Won This Auction!</h2>
              <p className="text-white/90 text-sm">Complete shipping & payment to claim your item</p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step === 'shipping' ? 'text-emerald-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step === 'payment' ? 'bg-emerald-500 text-white' : step === 'shipping' ? 'bg-emerald-500 text-white' : 'bg-gray-200'
            }`}>
              {step === 'payment' ? <Check className="h-5 w-5" /> : '1'}
            </div>
            <span className="font-medium">Shipping</span>
          </div>
          <div className="flex-1 h-1 bg-gray-200 rounded">
            <div className={`h-full bg-emerald-500 rounded transition-all ${step === 'payment' ? 'w-full' : 'w-0'}`} />
          </div>
          <div className={`flex items-center gap-2 ${step === 'payment' ? 'text-emerald-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step === 'payment' ? 'bg-emerald-500 text-white' : 'bg-gray-200'
            }`}>
              2
            </div>
            <span className="font-medium">Payment</span>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          {/* Order Summary */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <img 
                  src={order.listing_image} 
                  alt={order.listing_title}
                  className="w-full aspect-square rounded-lg object-cover"
                />
                <div>
                  <h3 className="font-semibold">{order.listing_title}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <User className="h-3 w-3" /> Sold by {order.seller_name}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Winning Bid</span>
                    <span className="font-medium">${order.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className={selectedShipping ? 'font-medium' : 'text-gray-500'}>
                      {selectedShipping ? `$${selectedShipping.price.toFixed(2)}` : 'Select below'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Sales Tax 
                      {order.tax_info && (
                        <span className="text-xs text-gray-400 ml-1">
                          ({(order.tax_info.total_rate * 100).toFixed(2)}%)
                        </span>
                      )}
                    </span>
                    <span className={taxAmount > 0 ? 'font-medium' : 'text-gray-500'}>
                      {taxAmount > 0 ? `$${taxAmount.toFixed(2)}` : 'Calculated at checkout'}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-emerald-600">${totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Content */}
          <Card className="md:col-span-3">
            {step === 'shipping' ? (
              /* Shipping Step */
              <>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5" /> Shipping Address
                  </CardTitle>
                  <CardDescription>Where should we send your item?</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Full Name *</Label>
                      <Input 
                        value={shippingAddress.name}
                        onChange={(e) => setShippingAddress(prev => ({...prev, name: e.target.value}))}
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Street Address *</Label>
                      <Input 
                        value={shippingAddress.street}
                        onChange={(e) => setShippingAddress(prev => ({...prev, street: e.target.value}))}
                        placeholder="123 Main St, Apt 4B"
                      />
                    </div>
                    <div>
                      <Label>City *</Label>
                      <Input 
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress(prev => ({...prev, city: e.target.value}))}
                        placeholder="San Francisco"
                      />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <Input 
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress(prev => ({...prev, state: e.target.value}))}
                        placeholder="CA"
                      />
                    </div>
                    <div>
                      <Label>ZIP Code *</Label>
                      <Input 
                        value={shippingAddress.zip_code}
                        onChange={(e) => setShippingAddress(prev => ({...prev, zip_code: e.target.value}))}
                        placeholder="94102"
                      />
                    </div>
                    <div>
                      <Label>Phone (optional)</Label>
                      <Input 
                        value={shippingAddress.phone}
                        onChange={(e) => setShippingAddress(prev => ({...prev, phone: e.target.value}))}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  {!shippingRates.length && (
                    <Button 
                      onClick={saveShippingAddress}
                      disabled={loadingRates}
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                    >
                      {loadingRates ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Truck className="h-4 w-4 mr-2" />}
                      Get Shipping Options
                    </Button>
                  )}

                  {/* Shipping Rates */}
                  {shippingRates.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Package className="h-4 w-4" /> Select Shipping Method
                      </h4>
                      <div className="space-y-2">
                        {shippingRates.map((rate, idx) => (
                          <div 
                            key={idx}
                            onClick={() => selectShipping(rate)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                              selectedShipping?.service === rate.service 
                                ? 'border-emerald-500 bg-emerald-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{rate.carrier} - {rate.service}</p>
                                <p className="text-sm text-gray-500">{rate.days} business days</p>
                              </div>
                              <p className="font-bold text-lg">${rate.price.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </>
            ) : (
              /* Payment Step */
              <>
                <CardHeader>
                  <CardTitle className="text-lg">Select Payment Method</CardTitle>
                  <CardDescription>Choose how you'd like to pay</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Shipping Summary */}
                  <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">
                        {selectedShipping?.carrier} {selectedShipping?.service} • {selectedShipping?.days} days
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setStep('shipping')}>
                      Change
                    </Button>
                  </div>

                  {/* Stripe Option */}
                  <div 
                    onClick={() => setSelectedMethod('stripe')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition ${
                      selectedMethod === 'stripe' 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="stripe-payment-option"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedMethod === 'stripe' ? 'border-emerald-500' : 'border-gray-300'
                      }`}>
                        {selectedMethod === 'stripe' && (
                          <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        )}
                      </div>
                      <CreditCard className="h-6 w-6 text-gray-700" />
                      <div className="flex-1">
                        <p className="font-medium">Credit / Debit Card</p>
                        <p className="text-sm text-gray-500">Pay securely with Stripe</p>
                      </div>
                    </div>
                  </div>

                  {/* PayPal Option */}
                  <div 
                    onClick={() => setSelectedMethod('paypal')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition ${
                      selectedMethod === 'paypal' 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="paypal-payment-option"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedMethod === 'paypal' ? 'border-emerald-500' : 'border-gray-300'
                      }`}>
                        {selectedMethod === 'paypal' && (
                          <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        )}
                      </div>
                      <div className="h-6 w-6 text-[#003087] flex items-center justify-center">
                        <PayPalLogo />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">PayPal</p>
                        <p className="text-sm text-gray-500">Pay with your PayPal account</p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-start gap-2 text-sm text-gray-500">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5" />
                    <p>Your payment is secure and encrypted.</p>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={handlePayment}
                    disabled={paymentLoading}
                    className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-lg"
                    data-testid="pay-now-button"
                  >
                    {paymentLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Pay ${totalAmount.toFixed(2)} Now
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardFooter>
              </>
            )}
          </Card>
        </div>

        {/* Timer Warning */}
        <Card className="mt-6 border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800 text-sm">
                <strong>Please complete payment within 24 hours.</strong> Unpaid orders may be cancelled.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderPaymentPage;
